class EsimMain extends HTMLElement {
    constructor() {
        super();
        this.swiper = null;
        this.searchInput = null;
        this.originalOrder = []; // 用于存储原始顺序
    }

    connectedCallback() {
        this.initHideExtraItems();
        this.cacheDOMElements();
        this.addEventListeners();
        this.initSwiper();
        this.matchMedia();
    }

    cacheDOMElements() {
        this.searchInput = this.query('.esim-main-header-search-input');
        this.switchItems = this.queryAll('.esim-switch-item:not(a.esim-switch-item)');
        this.showSwitchButtons = this.queryAll('.esim-show-switch-button');
        this.searchButton = this.query('.esim-main-header-search-button');
        this.searchAgainButton = this.query('.esim-search-again-button');
        this.switchContainer = this.query('.esim-main-header-switchs');
    }

    query(selector) {
        return this.querySelector(selector);
    }

    queryAll(selector) {
        return this.querySelectorAll(selector);
    }

    initSwiper() {
        const swiperContainer = this.query('.esim-main-items-container');
        if (swiperContainer) {
            this.swiper = new Swiper(swiperContainer, {
                slidesPerView: 1,
                spaceBetween: 30,
                effect: 'fade',
                fadeEffect: { crossFade: true },
                autoHeight: true,
                observer: true,
            });
        }
    }

    addEventListeners() {
        this.setupSwitchClick();
        this.setupShowSwitchButtonClick();
        this.setupSearch();
    }

    setupSwitchClick() {
        this.switchItems.forEach(item => {
            item.addEventListener('click', () => this.handleSwitchClick(item));
        });
    }

    handleSwitchClick(item) {
        this.switchItems.forEach(switchItem => switchItem.classList.remove('active'));
        item.classList.add('active');
        const index = parseInt(item.getAttribute('data-index'));
        this.swiper?.slideTo(index);
    }

    setupShowSwitchButtonClick() {
        this.showSwitchButtons.forEach(button => {
            button.addEventListener('click', () => this.handleShowSwitchButtonClick(button));
        });
    }

    handleShowSwitchButtonClick(button) {
        const parentContainer = button.closest('.swiper-slide');
        if (parentContainer) {
            const container = parentContainer.querySelector('.esim-main-item-container');
            button.classList.toggle('active');

            if (button.classList.contains('active')) {
                this.showAllItems(container);
                this.sortByName(container);  // 展开时排序
            } else {
                this.restoreOriginalOrder(container);  // 折叠前恢复顺序
                this.hideExtraItems(container);
            }

            this.changeButtonLabel(button);
            this.swiper.update();
        }
    }

    sortByName(container) {
        const items = Array.from(container.querySelectorAll('.esim-main-item'));
        const titles = items.map(item => {
            return {
                element: item,
                title: item.getAttribute('data-title').toLowerCase()
            };
        });

        // 按字母顺序排序
        titles.sort((a, b) => a.title.localeCompare(b.title));

        // 保存原始顺序
        this.originalOrder = items.map(item => item.cloneNode(true));

        // 重新排列
        titles.forEach((itemData, index) => {
            container.appendChild(itemData.element);  // 按顺序重新添加
        });
    }

    restoreOriginalOrder(container) {
        if (this.originalOrder.length > 0) {
            // 清空当前排序
            const items = container.querySelectorAll('.esim-main-item');
            items.forEach(item => item.remove());

            // 恢复原始顺序
            this.originalOrder.forEach(item => {
                container.appendChild(item);
            });

            // 清空原始顺序缓存
            this.originalOrder = [];
        }
    }

    getCSSVariable(container, variableName) {
        const style = window.getComputedStyle(container);
        return style.getPropertyValue(variableName).trim();
    }

    showAllItems(container) {
        const items = container.querySelectorAll('.esim-main-item');
        items.forEach(item => item.style.display = 'flex');
    }

    hideExtraItems(container) {
        const mediaQuery = window.matchMedia('(max-width: 749px)');
        const rowNum = this.getCSSVariable(container, '--row-num');
        const counts = mediaQuery.matches ? 20 : rowNum * 5;
        const items = container.querySelectorAll('.esim-main-item');

        items.forEach((item, index) => {
            item.style.display = (index < counts) ? 'flex' : 'none';
        });
    }

    changeButtonLabel(button) {
        button.textContent = button.classList.contains('active') ? button.getAttribute('data-less') : button.getAttribute('data-more');
    }

    setupSearch() {
        if (this.searchInput && this.searchButton) {
            this.searchButton.addEventListener('click', () => this.search());
            this.searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') this.search();
            });
        }

        if (this.searchAgainButton) {
            this.searchAgainButton.addEventListener('click', () => this.resetSearch());
        }
    }

    search() {
        const query = this.searchInput?.value.trim().toLowerCase();
        if (!query) return;

        const searchResultSlide = this.query('.search-result.swiper-slide');
        const items = searchResultSlide.querySelectorAll('.esim-main-item-container .esim-main-item');
        let hasResults = false;

        items.forEach(item => {
            const title = item.querySelector('.esim-main-item-title').textContent.toLowerCase();
            const isMatch = title.includes(query);
            item.style.display = isMatch ? 'flex' : 'none';
            if (isMatch) hasResults = true;
        });

        this.toggleSearchResultsDisplay(searchResultSlide, hasResults);
        this.swiper.slideTo(this.swiper.slides.length - 1);
        this.hideSwitchs();
    }

    toggleSearchResultsDisplay(searchResultSlide, hasResults) {
        const noResultElement = this.query('.esim-no-result');
        const headerElement = searchResultSlide.querySelector('.esim-main-item-box-header');

        noResultElement.style.display = hasResults ? 'none' : 'block';
        headerElement.style.display = hasResults ? '' : 'none';
    }

    hideSwitchs() {
        if (this.switchContainer) {
            this.switchContainer.style.opacity = '0';
        }
    }

    showAndInitSwitchs() {
        if (this.switchContainer) {
            this.switchContainer.style.opacity = '';
            this.switchContainer.querySelectorAll('.esim-switch-item').forEach((item, index) => {
                item.classList.toggle('active', index === 0);
            });
        }
    }

    resetSearch() {
        this.swiper.slideTo(0);
        this.showAndInitSwitchs();
        this.initSearchResult();
    }

    initSearchResult() {
        const searchResultSlide = this.query('.search-result.swiper-slide');
        const items = searchResultSlide.querySelectorAll('.esim-main-item-container .esim-main-item');

        setTimeout(() => {
            searchResultSlide.querySelector('.esim-main-item-box-header').style.display = '';
            this.query('.esim-no-result').style.display = 'none';

            items.forEach(item => {
                item.style.display = '';
            });
        }, 500);

        this.searchInput?.focus();
    }

    initHideExtraItems() {
        const slides = this.queryAll('.swiper-slide:not(.search-result)');
        slides.forEach(slide => {
            const container = slide.querySelector('.esim-main-item-container');
            if (container) {
                this.hideExtraItems(container);
            }
        });
    }

    matchMedia() {
        const updateItemsVisibility = () => {
            const slides = this.queryAll('.swiper-slide:not(.search-result)');
            slides.forEach(slide => {
                const container = slide.querySelector('.esim-main-item-container');
                const buttonActive = slide.querySelector('.esim-show-switch-button')?.classList.contains('active');
                if (container && !buttonActive) {
                    this.hideExtraItems(container);
                }
            });
        };

        const mediaQuery = window.matchMedia('(max-width: 1000px)');
        mediaQuery.addListener(updateItemsVisibility);
        updateItemsVisibility();
    }
}

customElements.define('esim-main', EsimMain);
